// @ts-nocheck
import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { getCroppedImg, CutoutOptions } from '../utils/imageUtils';
import { handleSliderPointerDown } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { CloseIcon, CheckmarkIcon } from './Icons';

export interface FreeformMediaCropperModalProps {
    image: string;
    originalFile?: File | Blob;
    onClose: () => void;
    onCropComplete: (blob: Blob) => void;
    title?: string;
}

export const PfpCropperModal: React.FC<FreeformMediaCropperModalProps> = ({
    image,
    originalFile,
    onClose,
    onCropComplete,
    title = 'Custom PFP & Sticker Creator'
}) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = currentUser ? usersData[currentUser] : null;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const isVideo = Boolean(originalFile?.type?.startsWith('video/') || image.startsWith('data:video/') || /\.(mp4|webm|ogg|mov|m4v)(?:\?|$)/i.test(image));
    const isGif = Boolean(originalFile?.type === 'image/gif' || image.startsWith('data:image/gif') || /\.gif(?:\?|$)/i.test(image));
    const isAnimated = isVideo || isGif;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(22, 27, 34, ${opacityVal})`;
        if (color.startsWith('#')) {
            const hex = color.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacityVal})`;
        }
        return color;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    // Cropper & Zoom state
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    // Cutout Tool Modes: 'crop' | 'threshold' | 'lasso' | 'eraser'
    const [activeTool, setActiveTool] = useState<'crop' | 'threshold' | 'lasso' | 'eraser'>('crop');

    // Auto Chroma-key Background Thresholding state & Real-time Live Preview Data URL
    const [bgThreshold, setBgThreshold] = useState<number>(0);
    const [thresholdPreviewUrl, setThresholdPreviewUrl] = useState<string | null>(null);
    const [isAiLoading, setIsAiLoading] = useState<boolean>(false);

    // Lasso / Polygon cutout points & completed mask
    const [lassoPoints, setLassoPoints] = useState<{ x: number, y: number }[]>([]);
    const [isDrawingLasso, setIsDrawingLasso] = useState<boolean>(false);

    // Brush Eraser / Restoration Canvas State & History Stack for Undo
    const [eraserSize, setEraserSize] = useState<number>(0);
    const [eraserMode, setEraserMode] = useState<'erase' | 'restore'>('erase');
    const [isDrawingEraser, setIsDrawingEraser] = useState<boolean>(false);
    const eraserCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const maskDataRef = useRef<ImageData | null>(null);
    const eraserHistoryRef = useRef<ImageData[]>([]);

    // Progress & Processing feedback
    const [isProcessing, setIsProcessing] = useState(false);
    const [progressPercent, setProgressPercent] = useState<number>(0);

    const onCropChange = (c: { x: number, y: number }) => setCrop(c);
    const onZoomChange = (z: number) => setZoom(z);

    const onCropCompleteInternal = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Explicit Touch & Scroll Prevention: Suppress pull-to-refresh & page swipe
    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        const prevOverscroll = document.body.style.overscrollBehavior;

        document.body.style.overflow = 'hidden';
        document.body.style.overscrollBehavior = 'none';
        document.documentElement.style.overscrollBehavior = 'none';

        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.style.overscrollBehavior = prevOverscroll;
            document.documentElement.style.overscrollBehavior = '';
        };
    }, []);

    // Real-Time High-Accuracy MediaPipe Selfie Segmentation (Instagram Stories Engine)
    useEffect(() => {
        if (bgThreshold === 0) {
            setThresholdPreviewUrl('');
            return;
        }

        let isSubscribed = true;
        setIsAiLoading(true);

        const runMediaPipeSegmentation = async () => {
            try {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = image;
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                });

                if (!isSubscribed) return;

                // Check if MediaPipe is available globally from script tag
                const windowMP = window as any;
                if (windowMP.SelfieSegmentation) {
                    const selfieSegmentation = new windowMP.SelfieSegmentation({
                        locateFile: (file: string) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
                    });

                    selfieSegmentation.setOptions({
                        modelSelection: 1, // General model (supports person + held items)
                    });

                    selfieSegmentation.onResults((results: any) => {
                        if (!isSubscribed) return;
                        const cvs = document.createElement('canvas');
                        cvs.width = img.width;
                        cvs.height = img.height;
                        const ctx = cvs.getContext('2d');
                        if (!ctx) return;

                        // 1. Draw segmentation mask
                        ctx.drawImage(results.segmentationMask, 0, 0, cvs.width, cvs.height);
                        const maskData = ctx.getImageData(0, 0, cvs.width, cvs.height);

                        // 2. Draw original image
                        ctx.clearRect(0, 0, cvs.width, cvs.height);
                        ctx.drawImage(img, 0, 0, cvs.width, cvs.height);
                        const imgData = ctx.getImageData(0, 0, cvs.width, cvs.height);

                        // 3. Composite 100% Solid Opaque Subject (Alpha = 255 where mask > 50%)
                        for (let i = 0; i < imgData.data.length; i += 4) {
                            const maskVal = maskData.data[i]; // Grayscale mask
                            if (maskVal > 100) {
                                imgData.data[i + 3] = 255; // 100% Solid subject
                            } else {
                                imgData.data[i + 3] = 0; // Transparent background
                            }
                        }

                        ctx.putImageData(imgData, 0, 0);
                        setThresholdPreviewUrl(cvs.toDataURL('image/png'));
                        setIsAiLoading(false);
                    });

                    await selfieSegmentation.send({ image: img });
                    return;
                }
            } catch (err) {
                console.warn('[PfpCropperModal] MediaPipe segmentation fallback:', err);
            } finally {
                if (isSubscribed) {
                    setIsAiLoading(false);
                }
            }
        };

        runMediaPipeSegmentation();

        return () => {
            isSubscribed = false;
        };
    }, [bgThreshold, image]);

    // Initialize Interactive Brush Canvas Layer & Save History
    useEffect(() => {
        if (activeTool === 'eraser' && eraserCanvasRef.current) {
            const cvs = eraserCanvasRef.current;
            const parent = cvs.parentElement;
            if (parent) {
                cvs.width = parent.clientWidth;
                cvs.height = parent.clientHeight;
                const ctx = cvs.getContext('2d');
                if (ctx && !maskDataRef.current) {
                    // Start fully transparent canvas mask layer (all pixels intact by default)
                    ctx.clearRect(0, 0, cvs.width, cvs.height);
                    const initialMask = ctx.getImageData(0, 0, cvs.width, cvs.height);
                    maskDataRef.current = initialMask;
                    eraserHistoryRef.current = [initialMask];
                }
            }
        }
    }, [activeTool]);

    // Touch & Mouse position helper
    const getNormalizedPos = (e: React.TouchEvent | React.MouseEvent, container: HTMLElement) => {
        const rect = container.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
        return {
            x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
            y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
        };
    };

    // Mobile & Desktop Interactive Lasso Drawing
    const handleLassoStart = (e: React.TouchEvent | React.MouseEvent) => {
        if (activeTool !== 'lasso') return;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        const pt = getNormalizedPos(e, e.currentTarget as HTMLElement);
        setIsDrawingLasso(true);
        setLassoPoints([pt]);
    };

    const handleLassoMove = (e: React.TouchEvent | React.MouseEvent) => {
        if (activeTool !== 'lasso' || !isDrawingLasso) return;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        const pt = getNormalizedPos(e, e.currentTarget as HTMLElement);
        setLassoPoints(prev => [...prev, pt]);
    };

    const handleLassoEnd = (e: React.TouchEvent | React.MouseEvent) => {
        if (activeTool !== 'lasso') return;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        setIsDrawingLasso(false);
    };

    // Mobile & Desktop Eraser/Restore Brush Drawing
    const handleEraserDraw = (e: React.TouchEvent | React.MouseEvent) => {
        if (activeTool !== 'eraser' || !eraserCanvasRef.current) return;
        const cvs = eraserCanvasRef.current;
        const ctx = cvs.getContext('2d');
        if (!ctx) return;

        const rect = cvs.getBoundingClientRect();
        const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
        const px = clientX - rect.left;
        const py = clientY - rect.top;

        ctx.beginPath();
        ctx.arc(px, py, eraserSize / 2, 0, Math.PI * 2);
        if (eraserMode === 'erase') {
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = 'rgba(239, 68, 68, 0.7)'; // Visual erased zone indicator
            ctx.fill();
        } else {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.fill();
        }
        maskDataRef.current = ctx.getImageData(0, 0, cvs.width, cvs.height);
    };

    const handleEraserStart = (e: React.TouchEvent | React.MouseEvent) => {
        if (activeTool !== 'eraser') return;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        setIsDrawingEraser(true);
        handleEraserDraw(e);
    };

    const handleEraserMove = (e: React.TouchEvent | React.MouseEvent) => {
        if (activeTool !== 'eraser' || !isDrawingEraser) return;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        handleEraserDraw(e);
    };

    const handleEraserEnd = (e: React.TouchEvent | React.MouseEvent) => {
        if (activeTool !== 'eraser') return;
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        if (isDrawingEraser && eraserCanvasRef.current) {
            setIsDrawingEraser(false);
            const ctx = eraserCanvasRef.current.getContext('2d');
            if (ctx) {
                const currentImageData = ctx.getImageData(0, 0, eraserCanvasRef.current.width, eraserCanvasRef.current.height);
                eraserHistoryRef.current.push(currentImageData);
            }
        }
    };

    // Undo last eraser/restore stroke
    const handleEraserUndo = () => {
        if (eraserHistoryRef.current.length > 1 && eraserCanvasRef.current) {
            eraserHistoryRef.current.pop(); // Remove current state
            const previousState = eraserHistoryRef.current[eraserHistoryRef.current.length - 1];
            const ctx = eraserCanvasRef.current.getContext('2d');
            if (ctx && previousState) {
                ctx.putImageData(previousState, 0, 0);
                maskDataRef.current = previousState;
            }
        }
    };

    // Handle Done / Processing with 0-100% progress bar
    const handleDone = async () => {
        if (!croppedAreaPixels && !isAnimated) return;
        setIsProcessing(true);
        setProgressPercent(5);

        try {
            // If animated (GIF or Video) and no custom destructive pixel mask was drawn, preserve the full animation!
            if (isAnimated && bgThreshold === 0 && lassoPoints.length === 0 && !maskDataRef.current) {
                if (originalFile) {
                    await onCropComplete(originalFile);
                } else {
                    const res = await fetch(image);
                    const blob = await res.blob();
                    await onCropComplete(blob);
                }
                return;
            }

            const cutoutOpts: CutoutOptions = {
                bgThreshold: bgThreshold,
                lassoPoints: lassoPoints.length > 2 ? lassoPoints : undefined,
                maskImageData: maskDataRef.current
            };

            const blob = await getCroppedImg(
                image,
                croppedAreaPixels || { x: 0, y: 0, width: 500, height: 500 },
                cutoutOpts,
                (pct) => setProgressPercent(pct)
            );

            if (blob) {
                await onCropComplete(blob);
            }
        } catch (e) {
            console.error('[PfpCropperModal] Cropping failed', e);
            alert('Failed to process image cutout.');
        } finally {
            setIsProcessing(false);
            setProgressPercent(0);
        }
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center z-[200] p-4 animate-fade-in backdrop-blur-xl bg-black/70 no-swipe select-none customize-post-modal-overlay"
        >
            <div
                className="rounded-[2.5rem] w-full max-w-lg overflow-hidden flex flex-col shadow-2xl border border-white/20 relative"
                style={{ backgroundColor: modalBg, color: userTextColor }}
            >
                {/* Header with Stickerized Title */}
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <h2
                            className="text-lg sm:text-xl font-black tracking-wide"
                            style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                        >
                            {title}
                        </h2>
                        {isAnimated && (
                            <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 font-bold uppercase tracking-wider">
                                {isVideo ? '🎬 Video' : '✨ Animated GIF'}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors"
                        disabled={isProcessing}
                    >
                        <CloseIcon className="w-5 h-5 text-white/80" />
                    </button>
                </div>

                {/* Main Canvas / Cropper Display Area */}
                <div className="relative w-full aspect-square bg-black/40 overflow-hidden flex items-center justify-center touch-none overscroll-none select-none" style={{ touchAction: 'none' }}>
                    {activeTool === 'crop' && (
                        <Cropper
                            image={isVideo ? undefined : image}
                            video={isVideo ? image : undefined}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            minZoom={0.5}
                            maxZoom={3.0}
                            onCropChange={onCropChange}
                            onCropComplete={onCropCompleteInternal}
                            onZoomChange={onZoomChange}
                            cropShape="round"
                            showGrid={false}
                            restrictPosition={false}
                        />
                    )}

                    {/* Interactive Lasso Cutout Canvas Layer */}
                    {activeTool === 'lasso' && (
                        <div
                            className="relative w-full h-full cursor-crosshair touch-none select-none flex items-center justify-center p-2"
                            style={{ touchAction: 'none' }}
                            onTouchStart={handleLassoStart}
                            onTouchMove={handleLassoMove}
                            onTouchEnd={handleLassoEnd}
                            onMouseDown={handleLassoStart}
                            onMouseMove={handleLassoMove}
                            onMouseUp={handleLassoEnd}
                        >
                            <img src={thresholdPreviewUrl || image} className="max-w-full max-h-full object-contain pointer-events-none" alt="Lasso preview" />
                            <svg className="absolute inset-0 w-full h-full pointer-events-none">
                                {lassoPoints.length > 1 && (
                                    <polygon
                                        points={lassoPoints.map(p => `${p.x * 100}% ${p.y * 100}%`).join(', ')}
                                        fill="rgba(59, 130, 246, 0.3)"
                                        stroke={userThemeColor}
                                        strokeWidth="3"
                                        strokeDasharray="6 4"
                                    />
                                )}
                            </svg>
                        </div>
                    )}

                    {/* Auto Chroma-key Background Thresholding Real-Time Live Preview */}
                    {activeTool === 'threshold' && (
                        <div className="relative w-full h-full flex items-center justify-center p-4">
                            <img
                                src={thresholdPreviewUrl || image}
                                className="max-w-full max-h-full object-contain rounded-2xl shadow-lg border border-white/10"
                                alt="Threshold preview"
                            />
                            {isAiLoading && (
                                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center space-y-3 z-20 rounded-2xl">
                                    <div className="w-8 h-8 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin" />
                                    <span className="text-xs font-bold text-white tracking-wide" style={stickerTextStyle(0.15)}>
                                        Generating AutoSticker AI Cutout...
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Eraser / Brush Canvas Layer */}
                    {activeTool === 'eraser' && (
                        <div
                            className="relative w-full h-full flex items-center justify-center p-4 touch-none select-none"
                            style={{ touchAction: 'none' }}
                            onTouchStart={handleEraserStart}
                            onTouchMove={handleEraserMove}
                            onTouchEnd={handleEraserEnd}
                            onMouseDown={handleEraserStart}
                            onMouseMove={handleEraserMove}
                            onMouseUp={handleEraserEnd}
                        >
                            <img src={thresholdPreviewUrl || image} className="max-w-full max-h-full object-contain rounded-2xl pointer-events-none" alt="Eraser preview" />
                            <canvas ref={eraserCanvasRef} className="absolute inset-0 w-full h-full cursor-crosshair" style={{ touchAction: 'none' }} />
                        </div>
                    )}

                    {/* Non-blocking Progress Overlay */}
                    {isProcessing && (
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-md flex flex-col items-center justify-center z-50 p-6 space-y-4">
                            <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden border border-white/20">
                                <div
                                    className="h-full transition-all duration-200"
                                    style={{ width: `${progressPercent}%`, backgroundColor: userThemeColor }}
                                />
                            </div>
                            <span className="text-sm font-bold tracking-wider" style={stickerTextStyle(0.18)}>
                                Processing Cutout... {progressPercent}%
                            </span>
                        </div>
                    )}
                </div>

                {/* Controls & Tool Panels */}
                <div className="p-5 space-y-4">
                    {/* Tool Tabs */}
                    <div className="grid grid-cols-4 gap-2 bg-black/20 p-1.5 rounded-2xl border border-white/10">
                        <button
                            onClick={() => setActiveTool('crop')}
                            className={`py-2 text-xs sm:text-sm font-bold rounded-xl transition-all ${activeTool === 'crop' ? 'bg-white/20 text-white shadow-md' : 'text-white/60 hover:text-white'}`}
                        >
                            🔍 Fit
                        </button>
                        <button
                            onClick={() => setActiveTool('threshold')}
                            className={`py-2 text-xs sm:text-sm font-bold rounded-xl transition-all ${activeTool === 'threshold' ? 'bg-white/20 text-white shadow-md' : 'text-white/60 hover:text-white'}`}
                        >
                            ✨ AutoSticker
                        </button>
                        <button
                            onClick={() => setActiveTool('lasso')}
                            className={`py-2 text-xs sm:text-sm font-bold rounded-xl transition-all ${activeTool === 'lasso' ? 'bg-white/20 text-white shadow-md' : 'text-white/60 hover:text-white'}`}
                        >
                            ✂️ Lasso
                        </button>
                        <button
                            onClick={() => setActiveTool('eraser')}
                            className={`py-2 text-xs sm:text-sm font-bold rounded-xl transition-all ${activeTool === 'eraser' ? 'bg-white/20 text-white shadow-md' : 'text-white/60 hover:text-white'}`}
                        >
                            ✏️ Eraser
                        </button>
                    </div>

                    {/* Active Tool Controls */}
                    {activeTool === 'crop' && (
                        <div className="flex items-center gap-4 bg-black/20 p-3 rounded-2xl border border-white/10">
                            <span className="text-xs font-bold text-white/80">Zoom</span>
                            <input
                                type="range"
                                value={zoom}
                                min={0.5}
                                max={3.0}
                                step={0.05}
                                aria-labelledby="Zoom"
                                onPointerDown={(e) => handleSliderPointerDown(e, zoom, 0.5, 3.0)}
                                onChange={(e) => setZoom(Number(e.target.value))}
                                className="flex-grow accent-blue-500"
                            />
                            <span className="text-xs font-mono text-white/70">{zoom.toFixed(2)}x</span>
                        </div>
                    )}

                    {activeTool === 'threshold' && (
                        <div className="flex items-center justify-between bg-black/20 p-3 rounded-2xl border border-white/10">
                            <span className="text-xs font-medium text-white/70">Automatic background removal (100% opaque subject cutout)</span>
                            <button
                                onClick={() => setBgThreshold(prev => prev > 0 ? 0 : 25)}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${bgThreshold > 0 ? 'bg-purple-600 text-white shadow-md' : 'bg-white/10 text-white hover:bg-white/20'}`}
                            >
                                {bgThreshold > 0 ? '✨ Auto Subject Cutout Active' : '⚡ Auto Cutout Subject'}
                            </button>
                        </div>
                    )}

                    {activeTool === 'lasso' && (
                        <div className="flex items-center justify-between bg-black/20 p-3 rounded-2xl border border-white/10">
                            <span className="text-xs font-medium text-white/70">Draw polygon outline to isolate sticker cutout (100% opaque inside)</span>
                            <button
                                onClick={() => setLassoPoints([])}
                                className="px-3 py-1 text-xs font-bold rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    )}

                    {activeTool === 'eraser' && (
                        <div className="flex items-center justify-between gap-3 bg-black/20 p-3 rounded-2xl border border-white/10">
                            <button
                                onClick={() => setEraserMode(prev => prev === 'erase' ? 'restore' : 'erase')}
                                className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                            >
                                {eraserMode === 'erase' ? '🧽 Erase' : '🖌️ Restore'}
                            </button>
                            <input
                                type="range"
                                value={eraserSize}
                                min={5}
                                max={60}
                                onChange={(e) => setEraserSize(Number(e.target.value))}
                                className="flex-grow accent-green-500"
                            />
                            <button
                                onClick={handleEraserUndo}
                                disabled={eraserHistoryRef.current.length <= 1}
                                className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-40"
                            >
                                ↩️ Undo
                            </button>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between gap-4 pt-2">
                        <button
                            onClick={onClose}
                            disabled={isProcessing}
                            className="flex-1 py-3 px-6 rounded-2xl bg-black/30 text-white font-bold hover:bg-black/40 border border-white/10 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDone}
                            disabled={isProcessing}
                            style={{ backgroundColor: userThemeColor }}
                            className="flex-1 py-3 px-6 rounded-2xl text-white font-bold hover:brightness-110 shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <CheckmarkIcon className="w-5 h-5 text-white" />
                            {isProcessing ? 'Processing...' : 'Apply Cutout'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
